/* PDP Zoom Lightbox
   Desktop/Tablet: opens at 1× (100% width), drag to pan.
   Mobile: opens at 1× (100% height), pinch to zoom, horizontal pan only — no vertical scroll.
   Slide change resets to 1×. Click/tap on slide does nothing.
*/
!function () {
  'use strict';

  var DRAG_THRESHOLD = 4;

  function isMobile() {
    return window.innerWidth < 750;
  }

  function getDefaultScale() {
    return 1;
  }

  class PdpZoom {
    constructor() {
      this.el = document.getElementById('pdp-zoom');
      if (!this.el) return;

      this.strip    = this.el.querySelector('.pdp-zoom__strip');
      this.main     = this.el.querySelector('.pdp-zoom__main');
      this.swiperEl = this.el.querySelector('.pdp-zoom__swiper');
      this.closeBtn = this.el.querySelector('.pdp-zoom__close');

      this.images  = [];
      this.current = 0;
      this.swiper  = null;

      this.scale  = getDefaultScale();
      this.panX   = 0;
      this.panY   = 0;

      this._startX        = 0;
      this._startY        = 0;
      this._wasDrag       = false;
      this._dragging      = false;
      this._dragPanStartX = 0;
      this._dragPanStartY = 0;

      this._pinching        = false;
      this._pinchStartDist  = 0;
      this._pinchStartScale = 1;
      this._pinchMidX       = 0;
      this._pinchMidY       = 0;
      this._pinchPanStartX  = 0;
      this._pinchPanStartY  = 0;

      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp   = this._onPointerUp.bind(this);
      this._onTouchStart  = this._onTouchStart.bind(this);
      this._onTouchMove   = this._onTouchMove.bind(this);
      this._onTouchEnd    = this._onTouchEnd.bind(this);

      this._bindGlobal();
    }

    // ─── Public ────────────────────────────────────────────────────────────────

    open(images, index) {
      this.images  = images;
      this.current = index || 0;

      this._buildSlides();
      this._buildStrip();
      this._initSwiper();
      this._syncStrip();

      this.el.classList.add('pdp-zoom--open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.closeBtn.focus();

      // Desktop: open in fit-to-height mode; click will enter pan mode
      if (!isMobile()) {
        this.el.classList.add('pdp-zoom--fit');
      }

      this._applyDefault(false);
    }

    close() {
      this._resetZoom();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.el.classList.remove('pdp-zoom--open', 'pdp-zoom--zoomed', 'pdp-zoom--fit');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    // ─── Slides ────────────────────────────────────────────────────────────────

    _buildSlides() {
      var defaultScale = getDefaultScale();
      var wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      wrapper.innerHTML = '';
      this.images.forEach(function (item) {
        var slide = document.createElement('div');
        slide.className = 'swiper-slide';
        var img = document.createElement('img');
        img.className       = 'pdp-zoom__img';
        img.src             = item.src;
        img.dataset.srcFull = PdpZoom._masterSrc(item.src);
        img.alt             = item.alt || '';
        img.draggable       = false;
        // Pre-apply default scale so the image is already scaled on first paint
        img.style.transform  = 'translate3d(0px,0px,0) scale(' + defaultScale + ')';
        img.style.transition = 'none';
        slide.appendChild(img);
        wrapper.appendChild(slide);
      });
    }

    static _masterSrc(src) {
      if (!src) return src;
      return src.replace(/(_\d+x\d*)(\.(?:jpg|jpeg|png|webp|gif))/i, '$2');
    }

    _initSwiper() {
      var self = this;
      this.swiper = new Swiper(this.swiperEl, {
        initialSlide: this.current,
        direction: 'horizontal',
        keyboard: { enabled: true, onlyInViewport: false },
        loop: false,
        allowTouchMove: false,
        simulateTouch: false,
        on: {
          slideChange: function () {
            self.current = this.activeIndex;
            self._syncStrip();
            // On desktop, go back to fit-to-height mode on slide change
            if (!isMobile()) {
              self.el.classList.add('pdp-zoom--fit');
            }
            self._applyDefault(false);
          }
        }
      });
    }

    // ─── Zoom state ────────────────────────────────────────────────────────────

    _getActiveImg() {
      if (!this.swiper) return null;
      var slide = this.swiper.slides[this.swiper.activeIndex];
      return slide ? slide.querySelector('.pdp-zoom__img') : null;
    }

    _getActiveSlide() {
      if (!this.swiper) return null;
      return this.swiper.slides[this.swiper.activeIndex] || null;
    }

    // Reset to default scale (1× desktop/tablet, 2× mobile), centred, no pan.
    _applyDefault(animate) {
      var img = this._getActiveImg();
      if (!img) return;

      if (img.dataset.srcFull && img.src !== img.dataset.srcFull) {
        img.src = img.dataset.srcFull;
      }

      var defaultScale   = getDefaultScale();
      this.scale         = defaultScale;
      this.panX          = 0;
      this.panY          = 0;
      this._pinching     = false;

      this.el.classList.add('pdp-zoom--zoomed');

      img.style.width      = '';
      img.style.height     = '';
      img.style.transition = animate ? 'transform .3s cubic-bezier(.25,.46,.45,.94)' : 'none';
      img.style.transform  = 'translate3d(0px,0px,0) scale(' + defaultScale + ')';

      if (this.swiper) this.swiper.allowTouchMove = false;
    }

    _resetZoom() {
      var img = this._getActiveImg();
      this.scale     = getDefaultScale();
      this.panX      = 0;
      this.panY      = 0;
      this._pinching = false;
      this.el.classList.remove('pdp-zoom--zoomed');
      if (img) {
        img.style.transition = 'none';
        img.style.transform  = 'translate3d(0,0,0) scale(1)';
        img.style.width      = '';
        img.style.height     = '';
      }
      if (this.swiper) this.swiper.allowTouchMove = false;
    }

    // Clamp pan so the scaled image never exposes the slide background.
    _clampPan(img) {
      var slide      = img.parentElement;
      var containerW = slide ? slide.clientWidth  : img.offsetWidth;
      var containerH = slide ? slide.clientHeight : img.offsetHeight;

      var naturalW = img.offsetWidth  || containerW;
      var naturalH = img.offsetHeight || containerH;

      var scaledW = naturalW * this.scale;
      var scaledH = naturalH * this.scale;

      var maxX = Math.max(0, (scaledW - containerW) / 2);
      var maxY = isMobile() ? 0 : Math.max(0, (scaledH - containerH) / 2);

      this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
      this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
    }

    _applyTransform(img, animate) {
      this._clampPan(img);
      var px = Math.round(this.panX);
      var py = Math.round(this.panY);
      img.style.transition = animate ? 'transform .3s cubic-bezier(.25,.46,.45,.94)' : 'none';
      img.style.transform  = 'translate3d(' + px + 'px,' + py + 'px,0) scale(' + this.scale + ')';
    }

    // ─── Thumb strip ───────────────────────────────────────────────────────────

    _buildStrip() {
      this.strip.innerHTML = '';
      var self = this;
      this.images.forEach(function (item, i) {
        var btn = document.createElement('button');
        btn.className = 'pdp-zoom__thumb';
        btn.setAttribute('role', 'listitem');
        btn.setAttribute('aria-label', 'Image ' + (i + 1));
        var img = document.createElement('img');
        img.src     = item.msrc || item.src;
        img.alt     = '';
        img.loading = 'lazy';
        btn.appendChild(img);
        btn.addEventListener('click', function () {
          if (self.swiper) {
            self._applyDefault(false);
            self.swiper.slideTo(i);
          }
        });
        self.strip.appendChild(btn);
      });
    }

    _syncStrip() {
      var current = this.current;
      this.strip.querySelectorAll('.pdp-zoom__thumb').forEach(function (btn, i) {
        btn.classList.toggle('pdp-zoom__thumb--active', i === current);
      });
      var active = this.strip.querySelectorAll('.pdp-zoom__thumb')[current];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    // ─── Events ────────────────────────────────────────────────────────────────

    _bindGlobal() {
      var self = this;

      this.closeBtn.addEventListener('click', function () { self.close(); });

      document.addEventListener('keydown', function (e) {
        if (!self.el.classList.contains('pdp-zoom--open')) return;
        if (e.key === 'Escape') self.close();
      });

      // ── Desktop: drag to pan ──────────────────────────────────────────────────
      this.main.addEventListener('pointerdown', function (e) {
        if (e.target.closest('.pdp-zoom__close')) return;
        if (e.pointerType === 'touch') return;
        // In fit-to-height mode, wait for click to enter pan mode first
        if (self.el.classList.contains('pdp-zoom--fit')) return;

        self._startX        = e.clientX;
        self._startY        = e.clientY;
        self._wasDrag       = false;
        self._dragging      = true;
        self._dragPanStartX = self.panX;
        self._dragPanStartY = self.panY;

        e.preventDefault();
        document.addEventListener('pointermove', self._onPointerMove);
        document.addEventListener('pointerup',   self._onPointerUp);
      });

      // Clicks on slide: toggle between fit-to-height and pan/zoom mode
      this.main.addEventListener('click', function (e) {
        if (e.target.closest('.pdp-zoom__close')) return;
        if (isMobile()) return;

        if (self.el.classList.contains('pdp-zoom--fit')) {
          // Enter pan/zoom mode
          self.el.classList.remove('pdp-zoom--fit');
          var img = self._getActiveImg();
          if (img) {
            img.style.width  = '';
            img.style.height = '';
          }
        } else if (!self._wasDrag) {
          // Pure click (no drag) while in pan mode → zoom back out to fit
          self.el.classList.add('pdp-zoom--fit');
          self._applyDefault(false);
        }
        // Reset drag flag after every click
        self._wasDrag = false;
      });

      // ── Mobile touch events ───────────────────────────────────────────────────
      this.main.addEventListener('touchstart', this._onTouchStart, { passive: false });
      this.main.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
      this.main.addEventListener('touchend',   this._onTouchEnd,   { passive: true  });
    }

    _onPointerMove(e) {
      if (!this._dragging) return;
      var dx = e.clientX - this._startX;
      var dy = e.clientY - this._startY;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        this._wasDrag = true;
      }
      this.panX = this._dragPanStartX + dx;
      this.panY = this._dragPanStartY + dy;
      var img = this._getActiveImg();
      if (img) this._applyTransform(img, false);
    }

    _onPointerUp() {
      this._dragging = false;
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup',   this._onPointerUp);
    }

    // ── Touch helpers ──────────────────────────────────────────────────────────

    _pinchDistance(t) {
      var dx = t[0].clientX - t[1].clientX;
      var dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    _pinchMidpoint(t) {
      return {
        x: (t[0].clientX + t[1].clientX) / 2,
        y: (t[0].clientY + t[1].clientY) / 2
      };
    }

    _onTouchStart(e) {
      var touches = e.touches;
      if (touches.length === 2) {
        e.preventDefault();
        this._pinching        = true;
        this._wasDrag         = true;
        this._pinchStartDist  = this._pinchDistance(touches);
        this._pinchStartScale = this.scale;
        var mid               = this._pinchMidpoint(touches);
        this._pinchMidX       = mid.x;
        this._pinchMidY       = mid.y;
        this._pinchPanStartX  = this.panX;
        this._pinchPanStartY  = this.panY;

        var img = this._getActiveImg();
        if (img) {
          var r = img.getBoundingClientRect();
          this._pinchImgCx = r.left + r.width  / 2;
          this._pinchImgCy = r.top  + r.height / 2;
        }
      } else if (touches.length === 1) {
        this._pinching      = false;
        this._startX        = touches[0].clientX;
        this._startY        = touches[0].clientY;
        this._wasDrag       = false;
        this._dragPanStartX = this.panX;
        this._dragPanStartY = this.panY;
      }
    }

    _onTouchMove(e) {
      var touches = e.touches;
      if (this._pinching && touches.length === 2) {
        e.preventDefault();
        var img = this._getActiveImg();
        if (!img) return;

        var dist     = this._pinchDistance(touches);
        var newScale = this._pinchStartScale * (dist / this._pinchStartDist);
        newScale = Math.max(1, Math.min(6, newScale));

        var ox = this._pinchImgCx;
        var oy = this._pinchImgCy;
        var dx = this._pinchMidX - ox;
        var dy = this._pinchMidY - oy;

        this.scale = newScale;
        this.panX  = this._pinchPanStartX - dx * (newScale - this._pinchStartScale);
        this.panY  = isMobile() ? 0 : this._pinchPanStartY - dy * (newScale - this._pinchStartScale);

        if (img.dataset.srcFull && img.src !== img.dataset.srcFull) {
          img.src = img.dataset.srcFull;
        }
        this._applyTransform(img, false);

      } else if (!this._pinching && touches.length === 1) {
        var dx = touches[0].clientX - this._startX;
        var dy = touches[0].clientY - this._startY;
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
          this._wasDrag = true;
        }
        if (this._wasDrag) {
          e.preventDefault();
          this.panX = this._dragPanStartX + dx;
          this.panY = isMobile() ? 0 : this._dragPanStartY + dy;
          var img = this._getActiveImg();
          if (img) this._applyTransform(img, false);
        }
      }
    }

    _onTouchEnd(e) {
      if (this._pinching) {
        this._pinching = false;
        // Pinched back to near 1× — snap back to default
        if (this.scale <= 1.1) {
          this._applyDefault(false);
        }
        return;
      }
      // Taps do nothing
    }
  }

  window.PdpZoom = null;
  function init() {
    window.PdpZoom = new PdpZoom();
  }
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
}();
